import { useEffect, useState } from "react";

import type { ActiveWhen } from "@/data/alarmRules/wireFormat";
import { cn } from "@/lib/cn";

import {
  WeeklyScheduleGrid,
  makeEmptyScheduleGrid,
  makeFullScheduleGrid,
} from "./WeeklyScheduleGrid";

interface Props {
  value: ActiveWhen | undefined;
  onChange: (next: ActiveWhen | undefined) => void;
}

function isoLocal(ms: number | undefined): string {
  if (!ms) return "";
  const d = new Date(ms);
  const pad = (n: number) => n.toString().padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function parseLocal(value: string): number | undefined {
  if (!value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  return d.getTime();
}

/**
 * Optional gate that restricts when a rule is evaluated. Toggling the
 * top switch removes the entire block; toggling individual sub-blocks
 * removes them in isolation. The editor mirrors the engine's v3
 * `_validate_active_when` validator: at least one of weeklySchedule /
 * dateRange must be present whenever `active_when` is set.
 */
export function ActiveWhenEditor({ value, onChange }: Props) {
  const enabled = value !== undefined;
  const [showSchedule, setShowSchedule] = useState(
    !!value?.weeklySchedule,
  );
  const [showRange, setShowRange] = useState(!!value?.dateRange);

  useEffect(() => {
    setShowSchedule(!!value?.weeklySchedule);
    setShowRange(!!value?.dateRange);
  }, [value]);

  const commit = (
    nextSchedule: boolean[][] | undefined,
    nextRange: { startMs?: number; endMs?: number } | undefined,
  ) => {
    if (!nextSchedule && !nextRange) {
      onChange(undefined);
      return;
    }
    onChange({
      weeklySchedule: nextSchedule,
      dateRange: nextRange,
    });
  };

  return (
    <div className="border border-edge rounded bg-surface-2/30 p-3">
      <label className="flex items-center gap-2 text-[12px]">
        <input
          type="checkbox"
          checked={enabled}
          onChange={(e) => {
            if (e.target.checked) {
              // Default: schedule with every hour active = effectively no gate
              // but lets the operator immediately edit cells.
              onChange({ weeklySchedule: makeFullScheduleGrid() });
              setShowSchedule(true);
              setShowRange(false);
            } else {
              onChange(undefined);
              setShowSchedule(false);
              setShowRange(false);
            }
          }}
        />
        <span>Restrict when this rule is active (active_when)</span>
      </label>

      {enabled && (
        <div className="mt-3 space-y-3">
          <div className="flex items-center gap-4 text-[11px]">
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={showSchedule}
                onChange={(e) => {
                  setShowSchedule(e.target.checked);
                  commit(
                    e.target.checked ? value?.weeklySchedule ?? makeFullScheduleGrid() : undefined,
                    showRange ? value?.dateRange : undefined,
                  );
                }}
              />
              Weekly schedule
            </label>
            <label className="flex items-center gap-1.5">
              <input
                type="checkbox"
                checked={showRange}
                onChange={(e) => {
                  setShowRange(e.target.checked);
                  commit(
                    showSchedule ? value?.weeklySchedule : undefined,
                    e.target.checked
                      ? value?.dateRange ?? { startMs: Date.now() }
                      : undefined,
                  );
                }}
              />
              Date range
            </label>
          </div>

          {showSchedule && (
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="text-[11px] text-text-faint">
                  Click and drag — blue hours are active.
                </span>
                <div className="flex gap-1">
                  <button
                    type="button"
                    onClick={() =>
                      commit(makeFullScheduleGrid(), showRange ? value?.dateRange : undefined)
                    }
                    className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border border-edge hover:bg-surface-2/60"
                  >
                    All on
                  </button>
                  <button
                    type="button"
                    onClick={() =>
                      commit(makeEmptyScheduleGrid(), showRange ? value?.dateRange : undefined)
                    }
                    className="text-[10px] uppercase tracking-wide px-2 py-1 rounded border border-edge hover:bg-surface-2/60"
                  >
                    All off
                  </button>
                </div>
              </div>
              <WeeklyScheduleGrid
                value={value?.weeklySchedule ?? makeFullScheduleGrid()}
                onChange={(next) =>
                  commit(next, showRange ? value?.dateRange : undefined)
                }
              />
            </div>
          )}

          {showRange && (
            <div className="grid grid-cols-2 gap-3">
              <label className="text-[11px] text-text-faint flex flex-col gap-1">
                Start (local)
                <input
                  type="datetime-local"
                  className="form-input"
                  value={isoLocal(value?.dateRange?.startMs)}
                  onChange={(e) =>
                    commit(showSchedule ? value?.weeklySchedule : undefined, {
                      ...(value?.dateRange ?? {}),
                      startMs: parseLocal(e.target.value),
                    })
                  }
                />
              </label>
              <label className="text-[11px] text-text-faint flex flex-col gap-1">
                End (local)
                <input
                  type="datetime-local"
                  className="form-input"
                  value={isoLocal(value?.dateRange?.endMs)}
                  onChange={(e) =>
                    commit(showSchedule ? value?.weeklySchedule : undefined, {
                      ...(value?.dateRange ?? {}),
                      endMs: parseLocal(e.target.value),
                    })
                  }
                />
              </label>
            </div>
          )}

          {!showSchedule && !showRange && (
            <p
              className={cn(
                "text-[11px] px-2 py-1 rounded border border-warn/40 bg-warn/10 text-warn",
              )}
            >
              Enable a weekly schedule or a date range — an empty
              active_when block is invalid.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
